import { TranslateService } from '@ngx-translate/core';

export type OrderTagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary';

const SYNC_STATUS_I18N_KEY: Record<string, string> = {
  PENDING_SYNC: 'orders.sync_pending',
  SYNCING: 'orders.sync_pending',
  WAITING_PARENT_SYNC: 'orders.sync_pending',
  SYNCED: 'orders.sync_synced',
  SYNC_FAILED: 'orders.sync_failed',
  SYNC_REJECTED: 'orders.sync_rejected',
  CANCELLED: 'orders.sync_cancelled',
};

export const PENDING_SYNC_STATUSES = ['PENDING_SYNC', 'SYNCING', 'WAITING_PARENT_SYNC'];
export const UNRESOLVED_SYNC_STATUSES = ['SYNC_FAILED', 'SYNC_REJECTED'];

/** Query params the footer sync badge sends to `/order-management` to open a local tab with a sync status preset. */
export type BacklogTab = 'pos' | 'returns';
export type BacklogSyncStatus = 'PENDING_SYNC' | 'SYNC_FAILED' | 'SYNC_REJECTED';

export function backlogQueryParams(tab: BacklogTab, syncStatus: BacklogSyncStatus): { tab: BacklogTab; syncStatus: BacklogSyncStatus } {
  return { tab, syncStatus };
}

export function parseBacklogQueryParams(tab: string | null, syncStatus: string | null): { tab: BacklogTab; syncStatus: BacklogSyncStatus } | null {
  const validTab = tab === 'pos' || tab === 'returns';
  const validStatus = syncStatus === 'PENDING_SYNC' || syncStatus === 'SYNC_FAILED' || syncStatus === 'SYNC_REJECTED';
  return validTab && validStatus ? { tab, syncStatus } : null;
}

export function getSyncStatusLabel(translate: TranslateService, syncStatus?: string): string {
  if (!syncStatus) return translate.instant('orders.sync_unknown');
  const key = SYNC_STATUS_I18N_KEY[syncStatus] || '';
  return key ? translate.instant(key) : syncStatus;
}

export function getSyncSeverity(syncStatus?: string): OrderTagSeverity {
  if (syncStatus === 'SYNCED') return 'success';
  if (syncStatus === 'CANCELLED') return 'secondary';
  if (syncStatus && UNRESOLVED_SYNC_STATUSES.includes(syncStatus)) return 'danger';
  if (syncStatus && PENDING_SYNC_STATUSES.includes(syncStatus)) return 'warning';
  return 'secondary';
}

const RETURN_BLOCKED_REASON_KEY: Record<string, string> = {
  PENDING_SYNC: 'orders.return_disabled_pending',
  SYNCING: 'orders.return_disabled_pending',
  WAITING_PARENT_SYNC: 'orders.return_disabled_pending',
  SYNC_FAILED: 'orders.return_disabled_failed',
  SYNC_REJECTED: 'orders.return_disabled_rejected',
};

/**
 * Translation key explaining why a POS order cannot be returned, or `null` when it can.
 * Only orders whose sync status is `SYNCED` (or cloud orders without a local status) qualify.
 */
export function getReturnDisabledReasonKey(syncStatus?: string | null): string | null {
  if (!syncStatus || syncStatus === 'SYNCED') return null;
  return RETURN_BLOCKED_REASON_KEY[syncStatus] ?? 'orders.return_disabled_not_synced';
}
